/**
 * Shared API error → human message helpers.
 *
 * Use `getApiErrorMessage(err, fallback)` in every mutation `onError` and
 * fetch `catch`. It understands:
 *  - axios errors from the orval client (`err.response.status/data`),
 *  - errors thrown by the hand-written fetch helpers in `src/config/api.ts`
 *    (`ApiRequestError`, or a plain `Error` whose message is already human),
 *  - the backend's error shapes: `{ error }`, `{ detail }`, `{ message }`,
 *    DRF field errors `{ field: ["msg"] }` and `{ non_field_errors: [...] }`.
 *
 * A 403 without a message keeps the old TrustCompliance wording ("Your company
 * must be approved…"). Network failures and 5xx get a friendly sentence
 * instead of axios' "Request failed with status code 500".
 *
 * `getApiFieldErrors(err)` returns `{ field: "first message" }` for forms that
 * show errors next to inputs.
 */

import i18n from "../i18n";

// Fallback sentences only — messages returned by the backend are shown as-is (not translated).
const companyNotApprovedMessage = (): string => i18n.t("common:errors.companyNotApproved");
const networkErrorMessage = (): string => i18n.t("common:errors.network");
const serverErrorMessage = (): string => i18n.t("common:errors.server");

/** Thrown by the fetch helpers in `src/config/api.ts` so callers keep the status + body. */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly data: unknown;

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.data = data;
  }
}

type ErrorLike = {
  message?: unknown;
  code?: unknown;
  status?: unknown;
  data?: unknown;
  request?: unknown;
  response?: { status?: unknown; data?: unknown };
  isAxiosError?: unknown;
};

const NON_FIELD_KEYS = new Set(["error", "detail", "message", "non_field_errors", "code"]);

const firstString = (value: unknown): string | null => {
  if (typeof value === "string") return value.trim() ? value : null;
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = firstString(item);
      if (found) return found;
    }
    return null;
  }
  if (value && typeof value === "object") {
    for (const item of Object.values(value as Record<string, unknown>)) {
      const found = firstString(item);
      if (found) return found;
    }
  }
  return null;
};

/** "companyIndustry" / "company_industry" → "Company industry". */
const humanizeField = (key: string): string => {
  const words = key
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * Pull a readable message out of a response body. Order: `error`, `detail`,
 * `message`, `non_field_errors`, then the first field error as
 * "Field name: message". Returns `fallback` when nothing usable is found.
 */
export function extractApiErrorMessage(data: unknown, fallback: string): string {
  if (typeof data === "string") {
    // Ignore HTML error pages; accept short plain-text bodies.
    return data.trim() && !data.trimStart().startsWith("<") && data.length < 300 ? data : fallback;
  }
  if (Array.isArray(data)) return firstString(data) ?? fallback;
  if (!data || typeof data !== "object") return fallback;

  const record = data as Record<string, unknown>;
  for (const key of ["error", "detail", "message", "non_field_errors"]) {
    const found = firstString(record[key]);
    if (found) return found;
  }
  for (const [key, value] of Object.entries(record)) {
    if (NON_FIELD_KEYS.has(key)) continue;
    const found = firstString(value);
    if (found) return `${humanizeField(key)}: ${found}`;
  }
  return fallback;
}

/** DRF-style field errors → `{ field: "first message" }` (empty when none). */
export function getApiFieldErrors(err: unknown): Record<string, string> {
  const { data } = readError(err);
  const out: Record<string, string> = {};
  if (!data || typeof data !== "object" || Array.isArray(data)) return out;
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    if (NON_FIELD_KEYS.has(key)) continue;
    const found = firstString(value);
    if (found) out[key] = found;
  }
  return out;
}

/** HTTP status of an API error, if there was a response. */
export function getApiErrorStatus(err: unknown): number | undefined {
  return readError(err).status;
}

/**
 * Backend-provided machine-readable error code (`{ "code": "..." }` in the
 * JSON body). Useful for differentiating distinct 403 reasons like
 * `readiness_required` from a generic "not allowed". Returns `null` when
 * the body isn't an object or carries no `code` string.
 */
export function getApiErrorCode(err: unknown): string | null {
  const { data } = readError(err);
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const code = (data as Record<string, unknown>).code;
  return typeof code === "string" && code.trim() ? code : null;
}

const readError = (err: unknown): { status?: number; data?: unknown; hasResponse: boolean } => {
  if (err instanceof ApiRequestError) return { status: err.status, data: err.data, hasResponse: true };
  if (!err || typeof err !== "object") return { hasResponse: false };
  const e = err as ErrorLike;
  if (e.response && typeof e.response === "object") {
    const status = typeof e.response.status === "number" ? e.response.status : undefined;
    return { status, data: e.response.data, hasResponse: true };
  }
  if (typeof e.status === "number") return { status: e.status, data: e.data, hasResponse: true };
  return { hasResponse: false };
};

const isCancelled = (err: unknown): boolean =>
  !!err &&
  typeof err === "object" &&
  ((err as ErrorLike).code === "ERR_CANCELED" || (err as Error).name === "CanceledError");

/**
 * The one helper pages should use to turn any API failure into a sentence
 * for the user. `fallback` is shown when the server gave nothing readable.
 */
export function getApiErrorMessage(err: unknown, fallback: string = i18n.t("common:errors.generic")): string {
  if (isCancelled(err)) return fallback;

  const { status, data, hasResponse } = readError(err);
  if (hasResponse) {
    if (status === 403) {
      return extractApiErrorMessage(data, companyNotApprovedMessage());
    }
    if (status !== undefined && status >= 500) {
      return extractApiErrorMessage(data, serverErrorMessage());
    }
    return extractApiErrorMessage(data, fallback);
  }

  if (err && typeof err === "object") {
    const e = err as ErrorLike;
    // axios: request was sent but no response came back.
    if (e.isAxiosError === true || e.request !== undefined) return networkErrorMessage();
  }
  // fetch() rejects with a TypeError on network failure.
  if (err instanceof TypeError) return networkErrorMessage();
  // Hand-written fetch helpers throw Error(humanMessage).
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
